export type GeoLocationAccuracy = 'HIGH' | 'ACCEPTABLE' | 'LOW' | 'POOR'

export type GeoLocationResult =
  | {
      success: true
      latitude: number
      longitude: number
      accuracy: number
      accuracyStatus: GeoLocationAccuracy
      timestamp: number
    }
  | {success: false; error: GeoLocationErrorType}

export type GeoLocationErrorType =
  | 'PERMISSION_DENIED'
  | 'POSITION_UNAVAILABLE'
  | 'TIMEOUT'
  | 'NOT_SUPPORTED'
  | 'UNKNOWN'

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
}

const getAccuracyStatus = (accuracyInMeters: number): GeoLocationAccuracy => {
  if (accuracyInMeters <= 50) return 'HIGH'
  if (accuracyInMeters <= 200) return 'ACCEPTABLE'
  if (accuracyInMeters <= 1000) return 'LOW'
  return 'POOR'
}

export const getCurrentLocation = async (): Promise<GeoLocationResult> => {
  // 1. Browser support check
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    return {success: false, error: 'NOT_SUPPORTED'}
  }

  // 2. Optional: check permission state
  try {
    if ('permissions' in navigator) {
      const permission = await navigator.permissions.query({
        name: 'geolocation' as PermissionName,
      })

      if (permission.state === 'denied') {
        return {success: false, error: 'PERMISSION_DENIED'}
      }
    }
  } catch {
    // permissions API not supported → continue
  }

  // 3. Wrap in Promise
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          success: true,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          accuracyStatus: getAccuracyStatus(position.coords.accuracy),
          timestamp: position.timestamp,
        })
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            resolve({success: false, error: 'PERMISSION_DENIED'})
            break
          case error.POSITION_UNAVAILABLE:
            resolve({success: false, error: 'POSITION_UNAVAILABLE'})
            break
          case error.TIMEOUT:
            resolve({success: false, error: 'TIMEOUT'})
            break
          default:
            resolve({success: false, error: 'UNKNOWN'})
        }
      },
      GEO_OPTIONS
    )
  })
}
